import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Row, Media } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { PlusCircle, Edit, ArrowRight, X } from 'react-feather';
import RulingDataModal from './RulingDataModal';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import FileCloseModal from './FileCloseModal';
import { fetchTableData, handleStateTransition } from '../../../../Api/ApiMethods';
import FilesCollapsible from '../../../Common/FilesCollapsible';

const RulingInformations = ({ arbitrationId, arbitrationData, refreshArbitrationData }) => {
    const [, setSelectedRows] = useState([]);
    const [clearSelectedRows] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedRuling, setSelectedRuling] = useState(null);

    // Masraf modal state'leri
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [currentRuling, setCurrentRuling] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [expenseModalMode, setExpenseModalMode] = useState('add');
    const [expenseSchemaType, setExpenseSchemaType] = useState('İtiraz Ödemesi'); // Dinamik schema type
    const [isExpenseReadOnly, setIsExpenseReadOnly] = useState(false);

    // Dosya kapatma modal state'leri
    const [isFileCloseModalOpen, setIsFileCloseModalOpen] = useState(false);

    const rulingApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/RulingInformation';

    const logSetData = (newData) => {
        setData(newData);
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    useEffect(() => {
        if (arbitrationId) {
            // Modify the API endpoint to include arbitrationId as a query parameter
            const modifiedApiEndpoint = `${rulingApiUrl}?arbitrationId=${arbitrationId}`;

            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '', // No search functionality
                setDataCallback: logSetData,
                setTotalRowsCallback: setTotalRows,
            });
        }
    }, [page, pageSize, arbitrationId, rulingApiUrl]);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedRuling(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedRuling(null);
        setModalOpen(true);
    };

    const openModifyModal = (ruling) => {
        setModalMode('modify');
        setSelectedRuling(ruling);
        setModalOpen(true);
    };

    const refreshRulingData = () => {
        if (arbitrationId) {
            const modifiedApiEndpoint = `${rulingApiUrl}?arbitrationId=${arbitrationId}`;

            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '',
                setDataCallback: logSetData,
                setTotalRowsCallback: setTotalRows,
            });
        }
        refreshArbitrationData();
    };

    // İtiraz masrafı handler fonksiyonları
    const handleAddObjectionExpenseModal = (ruling) => {
        setCurrentRuling(ruling);
        setSelectedExpense(null);
        setExpenseModalMode('add');
        setExpenseSchemaType('ITIRAZ_ODEMESI');
        setIsExpenseReadOnly(false);
        setIsExpenseModalOpen(true);
    };

    const handleEditObjectionExpenseModal = (ruling, expense) => {
        const currentState = arbitrationData?.arbitrationState?.title;
        const isItirazUcretiOdemesiBekleniyor = currentState === 'İtiraz Ücreti Ödemesi Bekleniyor';

        setCurrentRuling(ruling);
        setSelectedExpense(expense);
        setExpenseModalMode('modify');
        setExpenseSchemaType('ITIRAZ_ODEMESI');
        setIsExpenseReadOnly(!isItirazUcretiOdemesiBekleniyor); // Sadece "İtiraz Ücreti Ödemesi Bekleniyor" durumunda düzenlenebilir
        setIsExpenseModalOpen(true);
    };

    // Karşı vekalet masrafı handler fonksiyonları
    const handleAddCounterPoaExpenseModal = (ruling) => {
        setCurrentRuling(ruling);
        setSelectedExpense(null);
        setExpenseModalMode('add');
        setExpenseSchemaType('KARSI_VEKALET_ODEMESI');
        setIsExpenseReadOnly(false);
        setIsExpenseModalOpen(true);
    };

    const handleEditCounterPoaExpenseModal = (ruling, expense) => {
        const currentState = arbitrationData?.arbitrationState?.title;
        const isKarsiVekaletOdemesiBekleniyor = currentState === 'Karşı Vekalet Ödemesi Bekleniyor';

        setCurrentRuling(ruling);
        setSelectedExpense(expense);
        setExpenseModalMode('modify');
        setExpenseSchemaType('KARSI_VEKALET_ODEMESI');
        setIsExpenseReadOnly(!isKarsiVekaletOdemesiBekleniyor); // Sadece "Karşı Vekalet Ödemesi Bekleniyor" durumunda düzenlenebilir
        setIsExpenseModalOpen(true);
    };

    const closeExpenseModal = () => {
        setIsExpenseModalOpen(false);
        setCurrentRuling(null);
        setSelectedExpense(null);
        setExpenseSchemaType('ITIRAZ_ODEMESI'); // Default değere reset
    };

    const refreshExpenseData = () => {
        // Masraf eklendikten/güncellendiksinde tabloyu yenile
        refreshRulingData();
    };

    // State transition function
    const handleArbitrationStateTransition = async () => {
        try {
            // Karşı Vekalet Ödemesine Gönder için goToPayCounterPoaFee: true
            await handleStateTransition({
                arbitrationId,
                goToPayCounterPoaFee: true
            });
            refreshArbitrationData(); // Ana bileşendeki arbitration verisini yenile
        } catch (error) {
            // Toast mesajı handleStateTransition fonksiyonunda gösterildiği için burada tekrar göstermiyoruz
        }
    };

    // Objection button handler
    const handleObjectionTransition = async () => {
        try {
            // İtiraz Ödemesine Gönder için goToPayObjectionFee: true
            await handleStateTransition({
                arbitrationId,
                goToPayObjectionFee: true
            });
            refreshArbitrationData();
        } catch (error) {
        }
    };

    // Enforcement button handler
    const handleEnforcementTransition = async () => {
        try {
            // İcraya Gönder için goToEnforcement: true
            await handleStateTransition({
                arbitrationId,
                goToEnforcement: true
            });
            refreshArbitrationData();
        } catch (error) {
        }
    };

    // Dummy function for file closure (no functionality)
    const handleFileClose = () => {
        setIsFileCloseModalOpen(true);
    };

    // Function to check if file close button should be shown
    const shouldShowFileCloseButton = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        // Tekrar Başvuru Bekleniyor ve Kapama Bekleniyor durumlarında gösterme
        return !['Tekrar Başvuru Bekleniyor', 'Kapama Bekleniyor'].includes(currentState);
    };

    // Button configuration based on current state
    const getButtonConfig = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        const hasRulings = data && data.length > 0;

        const buttons = [];

        switch (currentState) {
            case 'Karar Bekleniyor':
                if (hasRulings) {
                    buttons.push({
                        key: 'stateTransition',
                        text: 'Karşı Vekalet Ödemesine Gönder',
                        action: handleArbitrationStateTransition,
                        className: 'btn-outline-success',
                        icon: 'ArrowRight'
                    });
                    buttons.push({
                        key: 'objection',
                        text: 'İtiraz Ücreti Ödemesine Gönder',
                        action: handleObjectionTransition,
                        className: 'btn-outline-warning',
                        icon: 'ArrowRight'
                    });
                    buttons.push({
                        key: 'enforcement',
                        text: 'İcraya Gönder',
                        action: handleEnforcementTransition,
                        className: 'btn-outline-info',
                        icon: 'ArrowRight'
                    });
                }
                break;

            case 'Karşı Vekalet Ödemesi Bekleniyor':
                buttons.push({
                    key: 'objection',
                    text: 'İtiraz Ücreti Ödemesine Gönder',
                    action: handleObjectionTransition,
                    className: 'btn-outline-warning',
                    icon: 'ArrowRight'
                });
                buttons.push({
                    key: 'enforcement',
                    text: 'İcraya Gönder',
                    action: handleEnforcementTransition,
                    className: 'btn-outline-info',
                    icon: 'ArrowRight'
                });
                break;

            case 'İtiraz Ücreti Ödemesi Bekleniyor':
                buttons.push({
                    key: 'stateTransition',
                    text: 'Karşı Vekalet Ödemesine Gönder',
                    action: handleArbitrationStateTransition,
                    className: 'btn-outline-success',
                    icon: 'ArrowRight'
                });
                buttons.push({
                    key: 'enforcement',
                    text: 'İcraya Gönder',
                    action: handleEnforcementTransition,
                    className: 'btn-outline-info',
                    icon: 'ArrowRight'
                });
                break;

            default:
                break;
        }

        return buttons;
    };

    const renderCellWithTooltip = (row, field, defaultValue = '') => {
        const value = row[field] || defaultValue;
        return (
            <div
                title={value}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                }}
            >
                {value}
            </div>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    const formatBoolean = (value) => {
        if (value === null || value === undefined) return '';
        return value ? 'Evet' : 'Hayır';
    };

    const columns = [
        {
            name: 'Geliş Tarihi',
            selector: row => formatDate(row.arrivalDate),
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Karar No',
            selector: row => row.rulingNumber || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'rulingNumber'),
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Karar Sonucu',
            selector: row => row.rulingResult || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'rulingResult'),
            grow: 1,
            minWidth: '150px'
        },
        {
            name: 'Karar Türü',
            selector: row => row.rulingType || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'rulingType'),
            grow: 1,
            minWidth: '130px'
        },
        {
            name: 'Ekspertiz Durumu',
            selector: row => row.appraisalFeeStatus || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'appraisalFeeStatus'),
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Karar İçin Eylem',
            selector: row => row.actionForRuling || '',
            sortable: true,
            cell: row => renderCellWithTooltip(row, 'actionForRuling'),
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Kabul Edilen Ücret',
            selector: row => formatCurrency(row.acceptedAppraisalFee),
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Vekalet Ücreti',
            selector: row => formatCurrency(row.powerOfAttorneyFee),
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Asıl Alacak',
            selector: row => formatCurrency(row.principalClaim),
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Karşı Vekalet Ödenecek',
            selector: row => formatBoolean(row.willPayCounterPowerOfAttorneyFee),
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'İtiraz',
            cell: (row) => {
                const objectionExpense = row.objectionExpenses; // İtiraz masrafı
                const currentState = arbitrationData?.arbitrationState?.title;
                const isItirazUcretiOdemesiBekleniyor = currentState === 'İtiraz Ücreti Ödemesi Bekleniyor';

                if (!objectionExpense) {
                    // Masraf yoksa ve uygun durumda "Ekle" butonu göster
                    if (isItirazUcretiOdemesiBekleniyor) {
                        return (
                            <div
                                className="btn btn-outline-success d-flex align-items-center justify-content-center"
                                onClick={() => handleAddObjectionExpenseModal(row)}
                                title="İtiraz Masrafı Ekle"
                                style={{ minWidth: '80px' }}
                            >
                                Ekle
                            </div>
                        );
                    } else {
                        return (
                            <div className="text-muted" style={{ minWidth: '80px', textAlign: 'center' }}>
                                -
                            </div>
                        );
                    }
                } else {
                    // Masrafı göster (sadece fiyat)
                    return (
                        <div
                            className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                            onClick={() => handleEditObjectionExpenseModal(row, objectionExpense)}
                            title={`${objectionExpense.expenseType || 'İtiraz Masrafı'} - Düzenlemek için tıklayın`}
                            style={{ cursor: 'pointer', minWidth: '80px' }}
                        >
                            <small className="text-primary fw-bold">
                                {objectionExpense.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(objectionExpense.amount) : '-'}
                            </small>
                        </div>
                    );
                }
            },
            ignoreRowClick: true,
            button: true,
            grow: 1.2,
            minWidth: '120px'
        },
        {
            name: 'Karşı Vekalet',
            cell: (row) => {
                const counterPoaExpense = row.counterPartyPoaExpenses; // Karşı vekalet masrafı
                const currentState = arbitrationData?.arbitrationState?.title;
                const isKarsiVekaletOdemesiBekleniyor = currentState === 'Karşı Vekalet Ödemesi Bekleniyor';

                if (!counterPoaExpense) {
                    // Masraf yoksa ve uygun durumda "Ekle" butonu göster
                    if (isKarsiVekaletOdemesiBekleniyor) {
                        return (
                            <div
                                className="btn btn-outline-success d-flex align-items-center justify-content-center"
                                onClick={() => handleAddCounterPoaExpenseModal(row)}
                                title="Karşı Vekalet Masrafı Ekle"
                                style={{ minWidth: '80px' }}
                            >
                                Ekle
                            </div>
                        );
                    } else {
                        return (
                            <div className="text-muted" style={{ minWidth: '80px', textAlign: 'center' }}>
                                -
                            </div>
                        );
                    }
                } else {
                    // Masrafı göster (sadece fiyat)
                    return (
                        <div
                            className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                            onClick={() => handleEditCounterPoaExpenseModal(row, counterPoaExpense)}
                            title={`${counterPoaExpense.expenseType || 'Karşı Vekalet Masrafı'} - Düzenlemek için tıklayın`}
                            style={{ cursor: 'pointer', minWidth: '80px' }}
                        >
                            <small className="text-primary fw-bold">
                                {counterPoaExpense.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(counterPoaExpense.amount) : '-'}
                            </small>
                        </div>
                    );
                }
            },
            ignoreRowClick: true,
            button: true,
            grow: 1.2,
            minWidth: '120px'
        },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
            grow: 1.2,
            minWidth: '100px'
        },
    ];

    return (
        <>
            <Row className="mt-4">
                <Col sm='12'>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                <Media body className='text-end mt-3'>
                                    <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                        <PlusCircle size={18} className="me-1" />
                                        {' Karar Ekle'}
                                    </div>
                                    {(() => {
                                        const buttons = getButtonConfig();

                                        return buttons.map(button => (
                                            <div
                                                key={button.key}
                                                className={`btn ms-2 ${button.className}`}
                                                onClick={button.action}
                                                style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                            >
                                                {button.icon === 'X' ?
                                                    <X size={18} className="me-1" /> :
                                                    <ArrowRight size={18} className="me-1" />
                                                }
                                                {button.text}
                                            </div>
                                        ));
                                    })()}
                                    {shouldShowFileCloseButton() && (
                                        <div
                                            className="btn btn-outline-secondary ms-2"
                                            onClick={handleFileClose}
                                            style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <X size={18} className="me-1" />
                                            Dosya Kapamaya Gönder
                                        </div>
                                    )}
                                </Media>
                            </div>

                            <div style={{ height: "40px" }}></div>

                            <div className='table-responsive'>
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    clearSelectedRows={clearSelectedRows}
                                    paginationTotalRows={totalRows}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                    striped
                                    noDataComponent={<div className="p-4 text-center">Karar bulunamadı.</div>}
                                    progressPending={!data}
                                    progressComponent={<p>Yükleniyor...</p>}
                                />
                            </div>

                            {Array.isArray(data) && data.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Dosyalar</h6>
                                    {data.map((row, idx) => (
                                        <FilesCollapsible
                                            key={row.id}
                                            title={row.rulingNumber ? `Karar: ${row.rulingNumber}` : `Karar #${idx + 1} Dosyaları`}
                                            referenceId={row.id}
                                        />
                                    ))}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            <RulingDataModal
                apiEndpoint={rulingApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedRuling={selectedRuling}
                refreshData={refreshRulingData}
                arbitrationId={arbitrationId}
            />

            {/* Masraf Ekleme/Düzenleme Modalı */}
            <ExpenseDataModal
                apiEndpoint={process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense'}
                isOpen={isExpenseModalOpen}
                toggle={closeExpenseModal}
                mode={expenseModalMode}
                selectedExpense={selectedExpense}
                refreshData={refreshExpenseData}
                customerId={null}
                autoAssignCustomer={false}
                accidentFile={[]}
                callformId={null}
                relatedArbitrationSchema={currentRuling ? [String(currentRuling.id)] : []}
                arbitrationSchemaType={expenseSchemaType}
                readOnlyView={isExpenseReadOnly}
            />

            {/* Dosya Kapatma Modalı */}
            <FileCloseModal
                isOpen={isFileCloseModalOpen}
                toggle={() => setIsFileCloseModalOpen(false)}
                arbitrationId={arbitrationId}
                refreshArbitrationData={refreshArbitrationData}
            />
        </>
    );
};

export default RulingInformations;
